import React from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useParams } from "react-router-dom";
import { Box, Typography, Container, Divider } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import Layout from "./componants/common/Layout";
import ScrollToTop from "./componants/common/ScrollToTop";

// Storefront Imports
import HomePage from "./componants/pages/HomePage/index";
import ShopPage from "./componants/pages/Shop/index";
import CategoryPage from "./componants/pages/CategoryPage/index";
import ProductDetail from "./componants/pages/ProductDetail/index";
import AboutPage from "./componants/pages/About/index";
import ProcessPage from "./componants/pages/Process/index";
import BlogPage from "./componants/pages/Blog/index";
import BlogPost from "./componants/pages/Blog/BlogDetail";
import CartPage from "./componants/pages/Cart/index";
import CheckoutPage from "./componants/pages/Checkout/index";
import OrderSuccess from "./componants/pages/OrderSuccess";
import LoginPage from "./componants/pages/LoginPage";
import AccountPage from "./componants/pages/Account/index";
import ProfileOverview from "./componants/pages/Account/ProfileOverview";
import OrderHistory from "./componants/pages/Account/OrderHistory";
import Wishlist from "./componants/pages/Account/Wishlist";
import AddressBook from "./componants/pages/Account/AddressBook";
import OrderTracking from "./componants/pages/Account/OrderTracking";
import ContactPage from "./componants/pages/Contact/index";
import FAQPage from "./componants/pages/FAQ/index";
import SearchPage from "./componants/pages/Search/SearchPage";
import TrackOrderPage from "./componants/pages/TrackOrder/index";
import NotFoundPage from "./componants/pages/NotFoundPage";

// Policies
import PrivacyPolicy from "./componants/pages/Policies/PrivacyPolicy";
import ReturnsRefund from "./componants/pages/Policies/ReturnsRefund";
import TermsConditions from "./componants/pages/Policies/TermsConditions";
import ShippingPolicy from "./componants/pages/Policies/ShippingPolicy";

// Dashboard Core
import DashHome from "./dashboard/pages/DashBoard";
import DashLogin from "./Authentication/Login";

// Orders Management
import DashOrderList from "./dashboard/pages/orders/OrderList";

// Tracking Management
import DashTrackingManager from "./dashboard/pages/tracking/TrackingManager";

// Invoice Management
import DashInvoiceList   from "./dashboard/pages/invoicePage/InvoiceDashboard";
import DashCreateInvoice from "./dashboard/pages/invoicePage/CreateInvoice";
import DashViewInvoice   from "./dashboard/pages/invoicePage/GetInvoice";

// Public Invoice View (customer-facing, no auth)
import InvoiceView from "./componants/pages/InvoiceView";

// Inventory
import DashProductList from "./dashboard/pages/inventory/ProductList";
import DashAddProduct from "./dashboard/pages/inventory/AddProduct";
import DashCategoryList from "./dashboard/pages/inventory/CategoryList";

// Leads
import DashLeadsList from "./dashboard/pages/leadManager/LeadsList";

// Employees
import DashUserList from "./dashboard/pages/employeeManager/UserList";
import DashAddUser from "./dashboard/pages/employeeManager/AddUser";

// Customers
import DashCustomerList from "./dashboard/pages/customerManager/CustomerList";

// Blogs
import DashBlogList from "./dashboard/pages/blog/BlogList";
import DashAddBlog from "./dashboard/pages/blog/AddBlog";
import DashBlogApproval from "./dashboard/pages/blog/BlogApprovalList";

// SEO
import DashAddSite from "./dashboard/pages/seoManager/AddSite";

import { useCookies } from "react-cookie";
import { config } from "./config/config";
import { Navigate } from "react-router-dom";
import { useUser } from "./context/UserContext";
import ProtectedRoute from "./componants/common/ProtectedRoute";
import OurServices from "./componants/pages/OurOils";
import { DashboardProvider } from "./context/DashboardContext";
import { WishlistProvider } from "./context/WishlistContext";

const AnimatedWrapper = ({ children }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.4 }}>
    {children}
  </motion.div>
);

const PublicRoute = ({ children }) => {
  const { user } = useUser();

  if (user) {
    const isDashboardLoggedOut = localStorage.getItem('ar_dashboard_logged_out') === 'true';
    if (isDashboardLoggedOut) {
      return children;
    }

    const rawRoles = user.roles || user.role || [];
    const userRoles = Array.isArray(rawRoles)
      ? rawRoles
      : typeof rawRoles === "string"
        ? rawRoles.split(",").map(r => r.trim())
        : [];

    const allowedStaffRoles = ["admin", "super admin", "team leader", "staff"];
    let isStaff = userRoles.some(role => 
      allowedStaffRoles.includes(String(role).trim().toLowerCase())
    );

    const knownAdminEmails = [
      "arinfotechsolutions38@gmail.com",
      "sales@arinfotechsolutions.com",
      "admin@arinfotechsolutions.com",
      "admin@arinfotech.com",
      "admin@coldpressedoils.in"
    ];
    if (!isStaff && user.email && knownAdminEmails.includes(user.email.toLowerCase())) {
      isStaff = true;
    }

    return <Navigate to={isStaff ? "/dashboard" : "/"} replace />;
  }

  return children;
};

const LeadWrapper = () => {
  const { status } = useParams();
  const formattedStatus = status.charAt(0).toUpperCase() + status.slice(1);
  return <DashLeadsList statusFilter={formattedStatus} />;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        {/* Storefront */}
        <Route path="/" element={<AnimatedWrapper><HomePage /></AnimatedWrapper>} />
        <Route path="/services" element={<AnimatedWrapper><OurServices /></AnimatedWrapper>} />
        <Route path="/shop" element={<AnimatedWrapper><ShopPage /></AnimatedWrapper>} />
        <Route path="/shop/:category" element={<AnimatedWrapper><CategoryPage /></AnimatedWrapper>} />
        <Route path="/product/:slug" element={<AnimatedWrapper><ProductDetail /></AnimatedWrapper>} />
        <Route path="/about" element={<AnimatedWrapper><AboutPage /></AnimatedWrapper>} />
        <Route path="/our-process" element={<AnimatedWrapper><ProcessPage /></AnimatedWrapper>} />
        <Route path="/blog" element={<AnimatedWrapper><BlogPage /></AnimatedWrapper>} />
        <Route path="/blog/:slug" element={<AnimatedWrapper><BlogPost /></AnimatedWrapper>} />
        <Route path="/cart" element={<AnimatedWrapper><CartPage /></AnimatedWrapper>} />
        <Route path="/checkout" element={<AnimatedWrapper><CheckoutPage /></AnimatedWrapper>} />
        <Route path="/order-success" element={<AnimatedWrapper><OrderSuccess /></AnimatedWrapper>} />
        <Route path="/login" element={<AnimatedWrapper><LoginPage /></AnimatedWrapper>} />
        <Route path="/account" element={<ProtectedRoute><AnimatedWrapper><AccountPage /></AnimatedWrapper></ProtectedRoute>}>
          <Route index element={<ProfileOverview />} />
          <Route path="orders" element={<OrderHistory />} />
          <Route path="wishlist" element={<Wishlist />} />
          <Route path="address" element={<AddressBook />} />
          <Route path="track" element={<OrderTracking />} />
          <Route path="tracking" element={<OrderTracking />} />
        </Route>
        <Route path="/contact" element={<AnimatedWrapper><ContactPage /></AnimatedWrapper>} />
        <Route path="/faq" element={<AnimatedWrapper><FAQPage /></AnimatedWrapper>} />
        <Route path="/search" element={<AnimatedWrapper><SearchPage /></AnimatedWrapper>} />
        <Route path="/track-order" element={<AnimatedWrapper><TrackOrderPage /></AnimatedWrapper>} />
        
        {/* Policies */}
        <Route path="/privacy-policy" element={<AnimatedWrapper><PrivacyPolicy /></AnimatedWrapper>} />
        <Route path="/returns-refund-policy" element={<AnimatedWrapper><ReturnsRefund /></AnimatedWrapper>} />
        <Route path="/terms-conditions" element={<AnimatedWrapper><TermsConditions /></AnimatedWrapper>} />
        <Route path="/shipping-policy" element={<AnimatedWrapper><ShippingPolicy /></AnimatedWrapper>} />

        {/* Dashboard Ecosystem */}
        <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
        <Route path="/admin/*" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard/login" element={<PublicRoute><DashLogin /></PublicRoute>} />

        <Route path="/dashboard" element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashHome /></ProtectedRoute>} />

        {/* Orders Management */}
        <Route path="/orders-manager/order-list" element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashOrderList /></ProtectedRoute>} />

        {/* Customers Management */}
        <Route path="/customer-manager/customers" element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashCustomerList /></ProtectedRoute>} />

        {/* Inventory Management */}
        <Route path="/inventory-manager/product-list" element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashProductList /></ProtectedRoute>} />
        <Route path="/inventory-manager/add-new-product" element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashAddProduct /></ProtectedRoute>} />
        <Route path="/inventory-manager/categories" element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashCategoryList /></ProtectedRoute>} />

        {/* Lead Management */}
        <Route path="/lead-manager/:status" element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><LeadWrapper /></ProtectedRoute>} />

        {/* Staff Management */}
        <Route path="/employee-manager/users" element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashUserList /></ProtectedRoute>} />
        <Route path="/employee-manager/add-user" element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashAddUser /></ProtectedRoute>} />

        {/* Content Management */}
        <Route path="/blog-manager/blog-list" element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashBlogList /></ProtectedRoute>} />
        <Route path="/blog-manager/add-blog" element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashAddBlog /></ProtectedRoute>} />

        {/* Editor's Queue */}
        <Route path="/blog-approval/blog-approve-list" element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashBlogApproval isApprovedView={false} /></ProtectedRoute>} />
        <Route path="/blog-approval/approved-blog-list" element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashBlogApproval isApprovedView={true} /></ProtectedRoute>} />

        {/* Marketing/SEO */}
        <Route path="/seo-manager/add-sites" element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashAddSite /></ProtectedRoute>} />

        {/* Tracking Management */}
        <Route path="/tracking-manager/list" element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashTrackingManager /></ProtectedRoute>} />
        <Route path="/tracking-manager/add" element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashTrackingManager /></ProtectedRoute>} />

        {/* Invoice Management */}
        <Route path="/invoice-manager/invoices"          element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashInvoiceList /></ProtectedRoute>} />
        <Route path="/invoice-manager/create-invoice"   element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashCreateInvoice /></ProtectedRoute>} />
        <Route path="/invoice-manager/view-invoice"     element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashViewInvoice /></ProtectedRoute>} />
        <Route path="/invoice-manager/edit-invoice/:id" element={<ProtectedRoute adminOnly={true} redirectTo="/dashboard/login"><DashCreateInvoice /></ProtectedRoute>} />

        {/* Public Invoice View — no auth required */}
        <Route path="/invoice/:token" element={<AnimatedWrapper><InvoiceView /></AnimatedWrapper>} />

        <Route path="*" element={<AnimatedWrapper><NotFoundPage /></AnimatedWrapper>} />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  return (
    <WishlistProvider>
      <Router>
        <ScrollToTop />
        <AppContent />
      </Router>
    </WishlistProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard') ||
    location.pathname.includes('-manager') ||
    location.pathname.includes('-approval') ||
    location.pathname.startsWith('/invoice-manager') ||
    location.pathname.startsWith('/tracking-manager');

  const isDashboardLogin = location.pathname === '/dashboard/login';

  return (
    <>
      {isDashboard ? (
        isDashboardLogin ? (
          <AnimatedRoutes />
        ) : (
          <DashboardProvider>
            <AnimatedRoutes />
          </DashboardProvider>
        )
      ) : (
        <Layout>
          <AnimatedRoutes />
        </Layout>
      )}
    </>
  );
}

export default App;
